import React, { useContext, useEffect, useRef, useState, Suspense } from 'react';
import { Container, List, Grid, Header, Segment, Dimmer, Loader } from 'semantic-ui-react';
import {Navigate, Route, Routes, useSearchParams, useNavigate} from 'react-router-dom';
import "./App.css"
import { StoryProvider } from './Context/StoryContext'
import { EnvironmentContext } from './Context/EnvironmentContext'
import { CreateProvider } from './Context/CreateContext';
import {
    auth,
    signOut,
    onAuthStateChanged,
    db,
    analytics,
    logEvent,
} from './firebaseConfig';
import { onSnapshot, doc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Navbar from './Navbar/Navbar';
import { checkAndAwardTokens } from './Utils/Utils';
import {useMediaQuery} from "react-responsive";
import CouponModal from './Modals/CouponModal';
import useCouponModal from './Modals/useCouponModal';
import CookieBanner from './Components/CookieBanner.jsx';
import { resetConsent } from './Utils/consent.js';
import ProtectedRoute from './Utils/ProtectedRoute';
// Gacha UX imports
import DailyRewardModal from './Payments/DailyRewardModal';
import LowCreditsModal from './Payments/LowCreditsModal';
import CreditNotification from './Payments/CreditNotification';
// Lazy-loaded pages
import {
  HomePage,
  CreatorLandingPage,
  AdminPage,
  Story,
  GameInterface,
  CreateListStories,
  SelectStories,
  Create,
  CreateOptions,
  StoryGenerator,
  Login,
  SignUp,
  ForgotPassword,
  Settings,
  AboutUs,
  ContactUs,
  TutoChooseModel,
  GettingStarted,
  DocumentationVideo,
  DocumentationTexteCarousel,
  GuideCreerCompte,
  ChoisirModele,
  ChoisirHistoire,
  CreerHistoire,
  DevenirExpert,
  CGU,
  IdentificationMention,
  PrivacyPolicy,
  BuyPage,
  TestMinio,
} from './Components/LazyPage';

/**
 * Loading fallback component for Suspense
 */
function PageLoader() {
  return (
    <Segment basic style={{ minHeight: '400px' }}>
      <Dimmer active inverted>
        <Loader size="large">Loading...</Loader>
      </Dimmer>
    </Segment>
  );
}

export default function App() {
    const location = useLocation();
    const refWhereToScroll = useRef(null);
    const env = useContext(EnvironmentContext);
    const [loading, setLoading] = useState(true);
    let unsubscribeUserSettings = () => { };
    let unsubscribeTokenCount = () => { };
    const isMobile = useMediaQuery({ maxWidth: 767 });
    const [searchParams] = useSearchParams();
    const isFlyMode = searchParams.get('fly')?.toLowerCase() === 'true';
    const navigate = useNavigate();

    const { t } = useTranslation();
    
    const {
        showCouponModal,
        setShowCouponModal,
        autoCoupon,
        handleRedeemCoupon,
    } = useCouponModal();

    useEffect(() => {
        env.setFlyMode(isFlyMode);
    }, [isFlyMode]);

    const handleSignOut = async () => {
        try {
            if (analytics) logEvent(analytics, 'user_SignOut');
            await signOut(auth);
        } catch (error) {
            console.error("Error signing out", error);
        }
    };

    const handleFullScreenChange = () => {
        env.setIsFullScreen(!!document.fullscreenElement);
    };

    useEffect(() => {
    if (analytics) logEvent(analytics, 'user_in_home_page');
        document.addEventListener("fullscreenchange", handleFullScreenChange);

        const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
            env.setUser(currentUser);
            setLoading(false);
            unsubscribeTokenCount();
            unsubscribeUserSettings();

            if (currentUser) {
                const userRef = doc(db, 'users', currentUser.uid);

                if (currentUser) {
                    // Check and award daily tokens, then show reward modal
                    checkAndAwardTokens(currentUser.uid).then((rewardData) => {
                        if (rewardData) {
                            env.setDailyRewardData(rewardData);
                            env.setShowDailyRewardModal(true);
                        }
                    });
                } else {
                    console.log("No user logged in");
                }

                // Subscribe to token count changes
                unsubscribeTokenCount = onSnapshot(userRef, (doc) => {
                    if (analytics) logEvent(analytics, 'token_count_changed');
                    if (doc.exists() && doc.data().tokenCount !== undefined) {
                        env.setUserTokenCount(doc.data().tokenCount);
                    } else {
                        env.setUserTokenCount(0);
                    }
                });

                // Subscribe to user settings changes (apiKey and model)
                unsubscribeUserSettings = onSnapshot(userRef, (doc) => {
                    if (analytics) logEvent(analytics, 'user_changed_model_or_api_key');
                    if (doc.exists()) {
                        const data = doc.data();
                        if (data.apiKey) env.setApiKey(data.apiKey);
                        if (data.model) env.setModelChosen(data.model);
                    }
                }, (error) => {
                    console.error("Error getting user settings:", error);
                });
            } else {
                if(env.user){
                    env.setUserTokenCount(0);
                    env.setApiKey('');
                    env.setModelChosen('mistral-small'); // reset to default or according to your app logic
                }
            }
        });

        return () => {
            document.removeEventListener("fullscreenchange", handleFullScreenChange);

            unsubscribeAuth();
            unsubscribeTokenCount();
            unsubscribeUserSettings();
        };
    }, [])
    ;
    useEffect(() => {
        if (!env.user) {
            if (analytics) logEvent(analytics, 'user_not_connected');
            if (!localStorage.getItem('tokensNotConnected')) {
                localStorage.setItem('tokensNotConnected', 500);
            }
            if (!localStorage.getItem('messagesNotConnected')) {
                localStorage.setItem('messagesNotConnected', 0);
            }
            if (!localStorage.getItem('messageLimitNotConnected')) {
                localStorage.setItem('messageLimitNotConnected', 5);
            }
            env.setUserTokenCount(parseInt(localStorage.getItem('tokensNotConnected')));
        }
      }, []);

    if (loading) {
        return; // Wait environment crucial values (user, token count, etc.) to be set before going anywhere
    }

    const fixed = true;
    return (
        <>
            <div
                className='flexyAppContainer'
                style={{ height: location.pathname.startsWith('/Story') ? "100vh" : "unset" }}
            >
                <CookieBanner />
                {!env.isFullScreen && (isFlyMode || !(location.pathname.startsWith('/Story'))) && (
                    <Navbar fixed={fixed} env={env} handleSignOut={handleSignOut} />
                )}
                {
                    env.showBuyModal && (
                        <div className="settings-modal-overlay" style={{flexDirection: 'column'}} onClick={() => env.setShowBuyModal(false)}>
                            <BuyPage/>
                            <p style={{color: "white"}}>{t('OrWait')}</p>
                        </div>
                    )
                }
                
                {/* Gacha UX Modals */}
                <DailyRewardModal
                    isOpen={env.showDailyRewardModal}
                    onClose={() => env.setShowDailyRewardModal(false)}
                    rewardData={env.dailyRewardData}
                />
                <LowCreditsModal
                    isOpen={env.showLowCreditsModal}
                    onClose={() => env.setShowLowCreditsModal(false)}
                    creditsNeeded={env.lowCreditsNeeded}
                />
                <CreditNotification
                    credits={env.creditNotification.credits}
                    isVisible={env.creditNotification.show}
                    type={env.creditNotification.type}
                    onComplete={() => env.setCreditNotification({ show: false, credits: 0, type: 'daily' })}
                />
                
                <CouponModal
                    open={showCouponModal}
                    onClose={() => setShowCouponModal(false)}
                    onRedeem={handleRedeemCoupon}
                    autoCoupon={autoCoupon}
                />
                <Container className={`main-content ${env.isFullScreen ? 'fullscreen' : ''}`}>
                    <Suspense fallback={<PageLoader />}>
                    <Routes>
                        <Route path="/" element={<HomePage />} />
                        <Route path="/CreateHomePage" element={<CreatorLandingPage />} />
                        <Route path="/AdminPage" element={
                            <ProtectedRoute>
                                <AdminPage />
                            </ProtectedRoute>} />
                        <Route path="/Story/:storyId" element={
                            <StoryProvider>
                                {isFlyMode && <Story />}
                                {!isFlyMode && <GameInterface />}
                            </StoryProvider>
                        } />
                        <Route path="/Create" element={
                            <ProtectedRoute>
                                <CreateListStories />
                            </ProtectedRoute>}
                        />
                         <Route path="/tests" element={
                            <ProtectedRoute>
                                <TestMinio />
                            </ProtectedRoute>}
                        />
                        <Route path="/Create/New" element={
                            <ProtectedRoute>
                                <CreateOptions />
                            </ProtectedRoute>} />
                        <Route path="/Create/Generate" element={
                            <ProtectedRoute>
                                <StoryProvider>
                                    <StoryGenerator />
                                </StoryProvider>
                            </ProtectedRoute>}
                        />

                        <Route path="/Create/Edit/:storyId" element={
                            <ProtectedRoute>
                                <StoryProvider>
                                    <CreateProvider>
                                        <Create />
                                    </CreateProvider>
                                </StoryProvider>
                            </ProtectedRoute>
                        } />

                        <Route path="/SelectStory" element={
                            <StoryProvider>
                                <SelectStories env={env} />
                            </StoryProvider>}
                        />
                        <Route path="/login" element={<Login />} />
                        <Route path="/signup" element={<SignUp />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/settings" element={
                            <ProtectedRoute>
                                <Settings env={env} />
                            </ProtectedRoute>} />
                        <Route path="/AboutUs" element={<AboutUs />} />
                        <Route path="/ContactUs" element={<ContactUs />} />
                        <Route path="/tutoChooseModel" element={<TutoChooseModel />} />
                        <Route path="/GettingStarted" element={<GettingStarted />} />
                        <Route path="/DocumentationVideo" element={<DocumentationVideo />} />
                        <Route path="/DocumentationTexte" element={<DocumentationTexteCarousel />} />
                        <Route path="/guide/creer-compte" element={<GuideCreerCompte />} />
                        <Route path="/guide/choisir-modele" element={<ChoisirModele />} />
                        <Route path="/guide/selectionner-histoire" element={<ChoisirHistoire />} />
                        <Route path="/guide/creer-histoire" element={<CreerHistoire />} />
                        <Route path="/guide/creation-parametres-avancés" element={<DevenirExpert />} />
                        <Route path="/CGU" element={<CGU />} />
                        <Route path="/IdentificationMention" element={<IdentificationMention />} />
                        <Route path="/Buy" element={
                            <ProtectedRoute>
                                <BuyPage />
                            </ProtectedRoute>} />
                        <Route path="/PrivacyPolicy" element={<PrivacyPolicy />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    </Suspense>
                </Container >

                <span ref={refWhereToScroll} />
                {
                    !env.isFullScreen && !(location.pathname.startsWith('/Story')) && (
                        <Segment inverted vertical style={{ backgroundColor: "#00201f" }}>
                            <Container >
                                <Grid divided inverted stackable>
                                    <Grid.Row centered>
                                        <Grid.Column width={3}>
                                            <Header inverted as='h4' content='About' />
                                            <List link inverted>
                                                <List.Item href="/AboutUs" as='a'>{t('AproposDeNous')}</List.Item>
                                                <List.Item href="/ContactUs" as='a'>{t('ContactUs')}</List.Item>
                                                <List.Item href="/CGU" as='a'>{t('CGU')}</List.Item>
                                                <List.Item href="/PrivacyPolicy" as='a'>{t('PrivacyPolicy')}</List.Item>
                                                <List.Item href="/IdentificationMention" as='a'>{t('IdentificationMention')}</List.Item>
                                            </List>
                                        </Grid.Column>
                                        <Grid.Column width={3}>
                                            <Header inverted as='h4' content={t('Documentation')} />
                                            <List link inverted>
                                                <List.Item href="/GettingStarted" as='a'>{t('GettingStarted')}</List.Item>
                                                <List.Item href="/DocumentationVideo" as='a'>{t('DocumentationVideo')}</List.Item>
                                                <List.Item href="/DocumentationTexte" as='a'>{t('DocumentationText')}</List.Item>
                                                {/* <List.Item href="/tutoChooseModel" as='a'>{t('ChoseRightModelForYou')}</List.Item> */}
                                            </List>
                                        </Grid.Column>
                                        <Grid.Column width={7}>
                                            <Header as='h4' inverted>
                                                Fablia
                                            </Header>
                                            <p>
                                                {t('StoriaDescription')}
                                            </p>
                                            <p>
                                                <a href="#" onClick={(e) => { e.preventDefault(); resetConsent(); window.location.reload(); }} style={{ color: '#fff', textDecoration: 'underline' }}>
                                                    {t('ManageCookies')}
                                                </a>
                                            </p>
                                        </Grid.Column>
                                    </Grid.Row>
                                </Grid>
                            </Container>
                        </Segment>
                    )
                }
            </div >
        </>
    );
}